// covered-by: frontends/carrie/src/utils/__tests__/askPunttLauncherSearch.test.ts
//
// Search params for ask-puntt list launchers (/projects, /chats).
//
// URL contract:
//   ?q=<text>           — filter query
//   ?people=id1,id2     — filter by person IDs
//   ?tags=tombstone     — filter by tag slugs
//   ?sort=updatedAt     — sort field
//   ?view=grid|list     — layout mode
//
// Legacy ?regions= and ?mine= in the URL are ignored (removed from product UX).
import { z } from "zod";

export type LauncherView = "grid" | "list";

export type LauncherSort = "updatedAt" | "createdAt" | "name";

export type LauncherSearch = {
  q?: string;
  people?: string[];
  tags?: string[];
  sort?: LauncherSort;
  view?: LauncherView;
};

export type LauncherSearchInput = Partial<LauncherSearch>;

const launcherViewSchema = z.enum(["grid", "list"]);
const launcherSortSchema = z.enum(["updatedAt", "createdAt", "name"]);

/** Parse a comma-separated string, JSON array string, or string[] into a trimmed array. */
function parseCommaSeparated(raw: unknown): string[] | undefined {
  // why: TanStack JSON-serializes string[] search as `["slug"]`; comma-splitting
  // that string keeps one bogus label and the list API returns no rows.
  if (Array.isArray(raw)) {
    const parts = raw
      .map((item) => (typeof item === "string" ? item.trim() : ""))
      .filter((s) => s.length > 0);
    return parts.length > 0 ? parts : undefined;
  }
  if (typeof raw !== "string") return undefined;
  const trimmed = raw.trim();
  if (trimmed.startsWith("[")) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        const parts = parsed
          .map((item) => (typeof item === "string" ? item.trim() : ""))
          .filter((s) => s.length > 0);
        return parts.length > 0 ? parts : undefined;
      }
    } catch {
      // fall through to comma-split
    }
  }
  const parts = trimmed
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  return parts.length > 0 ? parts : undefined;
}

/** Raw search record from TanStack validateSearch. */
export function parseLauncherSearch(
  search: Record<string, unknown>,
): LauncherSearch {
  const qRaw = typeof search.q === "string" ? search.q : "";
  const q = qRaw.trim().length > 0 ? qRaw.trim() : undefined;

  const viewRaw = search.view;
  const view =
    typeof viewRaw === "string" && launcherViewSchema.safeParse(viewRaw).success
      ? (viewRaw as LauncherView)
      : undefined;

  const people = parseCommaSeparated(search.people);
  const tags = parseCommaSeparated(search.tags);

  const sortRaw = search.sort;
  const sort =
    typeof sortRaw === "string" && launcherSortSchema.safeParse(sortRaw).success
      ? (sortRaw as LauncherSort)
      : undefined;

  return { q, people, tags, sort, view };
}

/** Search params for launcher route navigations. */
export function launcherSearch(input?: LauncherSearchInput): LauncherSearch {
  const raw = input ?? {};
  const q = raw.q?.trim() ? raw.q.trim() : undefined;
  const view =
    raw.view === "grid" || raw.view === "list" ? raw.view : undefined;
  const people =
    raw.people != null && raw.people.length > 0 ? raw.people : undefined;
  const tags = raw.tags != null && raw.tags.length > 0 ? raw.tags : undefined;
  const sort =
    raw.sort === "updatedAt" || raw.sort === "createdAt" || raw.sort === "name"
      ? raw.sort
      : undefined;
  return { q, people, tags, sort, view };
}

/**
 * Serialize LauncherSearch for the URL. TanStack router serializes arrays as
 * repeated keys by default; we use comma-separated for shorter URLs.
 */
export function serializeLauncherSearch(
  search: LauncherSearch,
): Record<string, string | undefined> {
  return {
    q: search.q,
    people: search.people?.join(","),
    tags: search.tags?.join(","),
    sort: search.sort,
    view: search.view,
  };
}
